import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Search,
  MessageSquare,
  ClipboardCheck,
  Cog,
  Package,
  Shield,
  History,
  Target,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import ContactForm from "@/components/ContactForm";
import Seo from "@/components/Seo";

const processSteps = [
  {
    icon: Search,
    title: "Identify Your Part",
    description:
      "Determine which component you need, including make, model, year, and variant details.",
  },
  {
    icon: MessageSquare,
    title: "Reach Out",
    description:
      "Contact us with your part specifications and any distinguishing attributes important to the design.",
  },
  {
    icon: ClipboardCheck,
    title: "Review & Quote",
    description:
      "We evaluate your request against our criteria and provide a detailed quote with timeline.",
  },
  {
    icon: Cog,
    title: "Production",
    description:
      "Our team begins the reproduction process with meticulous attention to detail and historical accuracy.",
  },
  {
    icon: Package,
    title: "Quality & Delivery",
    description:
      "Parts are thoroughly tested for quality and functionality before shipping to you.",
  },
];

const values = [
  {
    icon: Shield,
    title: "Quality First",
    description:
      "We will NEVER take your money knowing we cannot provide a high-quality product that meets our standards.",
  },
  {
    icon: History,
    title: "Historical Integrity",
    description:
      "Each reproduction is crafted with reverence for the original design and historical significance.",
  },
  {
    icon: Target,
    title: "Precision Manufacturing",
    description:
      "Currently capable of producing components with the strength of most steel forgings.",
  },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen pt-24" data-testid="about-page">
      <Seo
        title="About Us"
        description="Resurrected Gun Parts produces reproduction components for vintage military firearms, keeping historic weapons functional for collectors and restorers."
        path="/about"
      />
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#0f0f0f] border-b border-white/5">
        <div className="container-tactical">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <p className="font-mono text-[#C0A062] text-sm tracking-[0.3em] uppercase">
                Our Story
              </p>
              <h1 className="font-heading font-black text-5xl md:text-6xl text-white uppercase tracking-tighter">
                How Did It Start?
              </h1>
              <p className="text-[#A1A1AA] text-lg leading-relaxed">
                We know finding parts for obscure weapons is challenging;
                infuriating even sometimes. In an effort to reduce some of that
                frustration, we're here to get you from Point A (has no part and
                unpleasant to be around) to Point B (has part and an absolute
                delight to be around).
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="relative"
            >
              <div className="aspect-square rounded-sm overflow-hidden bg-[#121212] border border-white/5">
                <img
                  src="https://images.squarespace-cdn.com/content/v1/68885ac792afd429e43ec94a/14376194-00c3-4fde-9d26-b4b84365d99c/RGP+Logo.png"
                  alt="Resurrected Gun Parts"
                  className="w-full h-full object-contain p-12"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Origin Story */}
      <section className="section-spacing bg-[#0a0a0a]">
        <div className="container-tactical">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <p className="text-[#A1A1AA] text-lg leading-relaxed">
                This all started 10 years ago when family members of ours and
                avid NFA collectors started complaining about their struggle to
                locate parts for their WW1 and WW2 registered machine guns.
                Parts like <span className="text-white">MG-08/15 Maxim LMG Locks</span>,{" "}
                <span className="text-white">MG-34 ejector plates</span>,{" "}
                <span className="text-white">MP-35 Bergmann Firing Pins</span>,
                and so on were drying up or non-existent at all.
              </p>

              <p className="text-[#A1A1AA] text-lg leading-relaxed">
                Firearms like these and many more were commonly seen out at
                range days and their owners were happy to show them off, explain
                their respective histories, and even give others the opportunity
                to operate these historically significant pieces of machinery -
                that many would never see outside of a museum.
              </p>

              <div className="p-8 bg-[#121212] border-l-4 border-[#C0A062]">
                <p className="font-heading font-bold text-white text-xl md:text-2xl italic">
                  "What if someone started a company that specifically catered
                  to producing reproduction components to keep these
                  historically significant machines running — Forever?"
                </p>
              </div>

              <p className="text-[#A1A1AA] text-lg leading-relaxed">
                What started as a simple question took nearly a decade of
                watching more parts disappear to slowly encourage us to become a
                solution to the problem and no longer just a spectator.
              </p>

              <p className="text-[#A1A1AA] text-lg leading-relaxed">
                We are just starting out, but are building our catalog of parts,
                adding to it daily. Our commitment is to do our best to mitigate
                if not eliminate the possibility of someone's rare firearm
                remaining inoperable due to the impossibility of finding parts.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-spacing bg-[#0f0f0f]">
        <div className="container-tactical">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="font-mono text-[#C0A062] text-sm tracking-[0.3em] uppercase mb-4">
              Our Commitment
            </p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-white uppercase tracking-tight">
              What We Stand For
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, i) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-8 bg-[#121212] border border-white/5 hover:border-[#C0A062]/30 transition-colors"
                data-testid={`value-${i}`}
              >
                <div className="feature-icon mb-6">
                  <value.icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading font-bold text-white text-xl uppercase tracking-tight mb-3">
                  {value.title}
                </h3>
                <p className="text-[#A1A1AA] leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-spacing bg-[#0a0a0a]" data-testid="process-section">
        <div className="container-tactical">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="font-mono text-[#C0A062] text-sm tracking-[0.3em] uppercase mb-4">
              How It Works
            </p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-white uppercase tracking-tight">
              Our Process
            </h2>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {processSteps.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex gap-6 mb-8 last:mb-0"
                data-testid={`process-step-${i}`}
              >
                {/* Step Number */}
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-sm bg-[#C0A062] flex items-center justify-center">
                    <span className="font-heading font-black text-black text-lg">
                      {i + 1}
                    </span>
                  </div>
                  {i < processSteps.length - 1 && (
                    <div className="w-0.5 h-16 bg-white/10 mx-auto mt-2" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 pb-8">
                  <div className="flex items-center gap-3 mb-2">
                    <step.icon className="w-5 h-5 text-[#C0A062]" />
                    <h3 className="font-heading font-bold text-white text-lg uppercase tracking-tight">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-[#A1A1AA] leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Notes */}
      <section className="py-16 bg-[#121212] border-y border-white/5">
        <div className="container-tactical">
          <div className="max-w-4xl mx-auto">
            <h3 className="font-heading font-bold text-white text-2xl uppercase tracking-tight mb-8 text-center">
              Important Considerations
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 bg-[#0a0a0a] border border-white/5 rounded-sm">
                <p className="font-mono text-[#C0A062] text-xs uppercase tracking-wider mb-2">
                  Availability Check
                </p>
                <p className="text-[#A1A1AA] text-sm">
                  We will not reproduce parts that are presently widely
                  available for any reason.
                </p>
              </div>
              <div className="p-6 bg-[#0a0a0a] border border-white/5 rounded-sm">
                <p className="font-mono text-[#C0A062] text-xs uppercase tracking-wider mb-2">
                  Patent Restrictions
                </p>
                <p className="text-[#A1A1AA] text-sm">
                  Some modern weapons have restrictions that prohibit
                  reproduction of components.
                </p>
              </div>
              <div className="p-6 bg-[#0a0a0a] border border-white/5 rounded-sm">
                <p className="font-mono text-[#C0A062] text-xs uppercase tracking-wider mb-2">
                  Minor Fitting
                </p>
                <p className="text-[#A1A1AA] text-sm">
                  Some items may require minor fitting due to original
                  manufacturer tolerances or variants.
                </p>
              </div>
              <div className="p-6 bg-[#0a0a0a] border border-white/5 rounded-sm">
                <p className="font-mono text-[#C0A062] text-xs uppercase tracking-wider mb-2">
                  Quality Guarantee
                </p>
                <p className="text-[#A1A1AA] text-sm">
                  If we can't meet our standards of detail, durability, and
                  accuracy, we won't accept the job.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Program CTA */}
      <section className="section-spacing bg-[#0a0a0a]">
        <div className="container-tactical text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="font-mono text-[#C0A062] text-sm tracking-[0.3em] uppercase mb-4">
              Help Preserve History
            </p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-white uppercase tracking-tight mb-6">
              Join Our Partner Program
            </h2>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto mb-8">
              Have rare firearm parts in your collection? Put them to work. Loan
              us parts for reproduction and receive free parts in return.
            </p>
            <Link to="/partner-program" data-testid="about-partner-cta">
              <Button className="btn-primary">
                <span>Learn More</span>
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-spacing bg-[#0f0f0f]" data-testid="about-contact">
        <div className="container-tactical">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <p className="font-mono text-[#C0A062] text-sm tracking-[0.3em] uppercase">
                Get Started
              </p>
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-white uppercase tracking-tight">
                Contact Us
              </h2>
              <p className="text-[#A1A1AA] text-lg leading-relaxed">
                Ready to start your project? Have questions about our process?
                We're here to help bring your vintage firearm back to life.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-[#121212] border border-white/5 rounded-sm p-8"
            >
              <ContactForm />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
